import type { FunctionComponent, JSX } from "preact";
import { useComputed, useSignal } from "@preact/signals";
import { NumberInput } from "@easysaas/ui";
import {
  calculateCompetitorCost,
  calculateOurCost,
  calculateSavings,
  isValidInput,
  type CostBreakdown,
  type RawInput,
  type Savings,
} from "./calc";

export interface PaymentComparisonProps {
  currency: string;
  ourRate: number;
  monthlyVolume: number;
  monthlyTransactions: number;
  privateDebitPercent: number;
  privateDebitShare: number;
  privateCreditPercent: number;
  privateCreditShare: number;
  businessPercent: number;
  businessShare: number;
  paymentFixed: number;
  payoutFixed: number;
  payoutPercent: number;
  payoutsPerMonth: number;
  threeDsFee: number;
  accountMonthly: number;
  terminalMonthly: number;
}

const formatCurrency = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

const formatPercent = (value: number): string =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value) + " %";

const formatPercentInteger = (value: number): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value) + " %";

interface ComparisonResult {
  competitor: CostBreakdown;
  ours: CostBreakdown;
  savings: Savings;
}

interface RowProps {
  label: string;
  tooltip: string;
  value: number | "";
  onValue: (v: number | "") => void;
  step: number;
  ourValue: string;
}

const Row = ({ label, tooltip, value, onValue, step, ourValue }: RowProps): JSX.Element => (
  <div class="cmp-row">
    <div class="cmp-row-input">
      <NumberInput
        label={label}
        tooltip={tooltip}
        value={value}
        onValue={onValue}
        min={0}
        step={step}
      />
    </div>
    <div class="cmp-row-our" aria-hidden="true">
      {ourValue}
    </div>
  </div>
);

interface PairedRowProps {
  primaryLabel: string;
  primaryTooltip: string;
  primaryValue: number | "";
  primaryOnValue: (v: number | "") => void;
  primaryStep: number;
  secondaryLabel: string;
  secondaryTooltip: string;
  secondaryValue: number | "";
  secondaryOnValue: (v: number | "") => void;
  secondaryStep: number;
  ourValue: string;
}

const PairedRow = ({
  primaryLabel,
  primaryTooltip,
  primaryValue,
  primaryOnValue,
  primaryStep,
  secondaryLabel,
  secondaryTooltip,
  secondaryValue,
  secondaryOnValue,
  secondaryStep,
  ourValue,
}: PairedRowProps): JSX.Element => (
  <div class="cmp-row cmp-row--paired">
    <NumberInput
      label={primaryLabel}
      tooltip={primaryTooltip}
      value={primaryValue}
      onValue={primaryOnValue}
      min={0}
      step={primaryStep}
    />
    <NumberInput
      label={secondaryLabel}
      tooltip={secondaryTooltip}
      value={secondaryValue}
      onValue={secondaryOnValue}
      min={0}
      step={secondaryStep}
    />
    <div class="cmp-row-our" aria-hidden="true">
      {ourValue}
    </div>
  </div>
);

const SHARE_TOOLTIP =
  "Anteil dieses Karten-Typs am Gesamtvolumen. Defaults: Privat Debit 50 %, Privat Kredit 40 %, Business 10 %. Die Werte werden intern auf 100 % normalisiert — du kannst also auch andere Verhältnisse eingeben.";

const Widget: FunctionComponent<PaymentComparisonProps> = (props) => {
  const { currency, ourRate } = props;
  const monthlyVolume = useSignal<number | "">(props.monthlyVolume);
  const monthlyTransactions = useSignal<number | "">(props.monthlyTransactions);
  const privateDebitPercent = useSignal<number | "">(props.privateDebitPercent);
  const privateDebitShare = useSignal<number | "">(props.privateDebitShare);
  const privateCreditPercent = useSignal<number | "">(props.privateCreditPercent);
  const privateCreditShare = useSignal<number | "">(props.privateCreditShare);
  const businessPercent = useSignal<number | "">(props.businessPercent);
  const businessShare = useSignal<number | "">(props.businessShare);
  const paymentFixed = useSignal<number | "">(props.paymentFixed);
  const payoutFixed = useSignal<number | "">(props.payoutFixed);
  const payoutPercent = useSignal<number | "">(props.payoutPercent);
  const payoutsPerMonth = useSignal<number | "">(props.payoutsPerMonth);
  const threeDsAvgPerTx = useSignal<number | "">(props.threeDsFee);
  const accountMonthly = useSignal<number | "">(props.accountMonthly);
  const terminalMonthly = useSignal<number | "">(props.terminalMonthly);

  const result = useComputed<ComparisonResult | null>(() => {
    const raw: RawInput = {
      monthlyVolume: monthlyVolume.value,
      monthlyTransactions: monthlyTransactions.value,
      privateDebitPercent: privateDebitPercent.value,
      privateDebitShare: privateDebitShare.value,
      privateCreditPercent: privateCreditPercent.value,
      privateCreditShare: privateCreditShare.value,
      businessPercent: businessPercent.value,
      businessShare: businessShare.value,
      paymentFixed: paymentFixed.value,
      payoutFixed: payoutFixed.value,
      payoutPercent: payoutPercent.value,
      payoutsPerMonth: payoutsPerMonth.value,
      threeDsAvgPerTx: threeDsAvgPerTx.value,
      accountMonthly: accountMonthly.value,
      terminalMonthly: terminalMonthly.value,
      ourPercent: ourRate,
    };
    if (!isValidInput(raw)) return null;
    const competitor = calculateCompetitorCost(raw);
    const ours = calculateOurCost(raw);
    const savings = calculateSavings(competitor, ours);
    return { competitor, ours, savings };
  });

  const shareSum = useComputed(() => {
    const a = privateDebitShare.value;
    const b = privateCreditShare.value;
    const c = businessShare.value;
    if (typeof a !== "number" || typeof b !== "number" || typeof c !== "number") return null;
    return a + b + c;
  });

  const savingsLine = useComputed(() => {
    const r = result.value;
    if (r === null) return null;
    if (r.savings.absolute > 0) {
      return {
        tone: "positive" as const,
        text: `Sie sparen ${formatCurrency(r.savings.absolute, currency)} pro Monat (${formatPercent(r.savings.percent)})`,
      };
    }
    if (r.savings.absolute < 0) {
      return {
        tone: "negative" as const,
        text: `Mitbewerber ist ${formatCurrency(-r.savings.absolute, currency)} pro Monat günstiger`,
      };
    }
    return { tone: "neutral" as const, text: "Beide Anbieter kosten gleich viel" };
  });

  const ourPercentText = formatPercent(ourRate);
  const zeroEur = formatCurrency(0, currency);
  const zeroPercent = formatPercent(0);
  const dash = "—";

  return (
    <div class="root">
      <h3 class="title">Payment-Kosten-Vergleich</h3>
      <p class="subtitle">
        Tragen Sie die Werte Ihres aktuellen Anbieters ein. Wir vergleichen das mit der Flatrate von{" "}
        {ourPercentText} pro Zahlung von easyWerkstatt Payments.
      </p>

      <fieldset class="fieldset">
        <legend class="legend">Dein Volumen</legend>
        <div class="grid">
          <NumberInput
            label="Volumen pro Monat (€)"
            tooltip="Gesamter Umsatz, den du pro Monat über deinen Payment-Provider abwickelst (alle Karten- und Online-Zahlungen zusammen)."
            value={monthlyVolume.value}
            onValue={(v) => {
              monthlyVolume.value = v;
            }}
            min={0}
            step={100}
          />
          <NumberInput
            label="Anzahl Zahlungen pro Monat"
            tooltip="Wie viele einzelne Transaktionen pro Monat? Wichtig für die Fixgebühr je Zahlung."
            value={monthlyTransactions.value}
            onValue={(v) => {
              monthlyTransactions.value = v;
            }}
            min={0}
            step={1}
          />
        </div>
      </fieldset>

      <fieldset class="fieldset">
        <legend class="legend">Zahlungsgebühren</legend>
        <div class="cmp-headers">
          <span class="cmp-header-input">Mitbewerber</span>
          <span class="cmp-header-our">easyWerkstatt Payments</span>
        </div>
        <div class="cmp-rows">
          <PairedRow
            primaryLabel="Privat Debit (%)"
            primaryTooltip="Prozentsatz pro Zahlung mit privaten Debitkarten (z. B. Bankomatkarte/Maestro)."
            primaryValue={privateDebitPercent.value}
            primaryOnValue={(v) => {
              privateDebitPercent.value = v;
            }}
            primaryStep={0.01}
            secondaryLabel="Anteil (%)"
            secondaryTooltip={SHARE_TOOLTIP}
            secondaryValue={privateDebitShare.value}
            secondaryOnValue={(v) => {
              privateDebitShare.value = v;
            }}
            secondaryStep={1}
            ourValue={ourPercentText}
          />
          <PairedRow
            primaryLabel="Privat Kredit (%)"
            primaryTooltip="Prozentsatz pro Zahlung mit privaten Kreditkarten (Visa, Mastercard, etc.)."
            primaryValue={privateCreditPercent.value}
            primaryOnValue={(v) => {
              privateCreditPercent.value = v;
            }}
            primaryStep={0.01}
            secondaryLabel="Anteil (%)"
            secondaryTooltip={SHARE_TOOLTIP}
            secondaryValue={privateCreditShare.value}
            secondaryOnValue={(v) => {
              privateCreditShare.value = v;
            }}
            secondaryStep={1}
            ourValue={ourPercentText}
          />
          <PairedRow
            primaryLabel="Business (%)"
            primaryTooltip="Prozentsatz pro Zahlung mit Firmenkarten / Geschäftskarten — typischerweise teurer als Privatkarten."
            primaryValue={businessPercent.value}
            primaryOnValue={(v) => {
              businessPercent.value = v;
            }}
            primaryStep={0.01}
            secondaryLabel="Anteil (%)"
            secondaryTooltip={SHARE_TOOLTIP}
            secondaryValue={businessShare.value}
            secondaryOnValue={(v) => {
              businessShare.value = v;
            }}
            secondaryStep={1}
            ourValue={ourPercentText}
          />
          <p
            class={`share-sum${shareSum.value !== null && shareSum.value !== 100 ? " share-sum--off" : ""}`}
          >
            {shareSum.value === null
              ? "Anteile bitte ausfüllen"
              : shareSum.value === 100
                ? `Summe der Anteile: ${formatPercentInteger(shareSum.value)}`
                : `Summe der Anteile: ${formatPercentInteger(shareSum.value)} — wird auf 100 % normalisiert`}
          </p>
          <Row
            label="Fixgebühr je Zahlung (€)"
            tooltip="Fixbetrag pro Transaktion, den dein Anbieter zusätzlich zur Prozent-Gebühr berechnet. Gilt für alle Karten-Typen gleich."
            value={paymentFixed.value}
            onValue={(v) => {
              paymentFixed.value = v;
            }}
            step={0.01}
            ourValue={zeroEur}
          />
        </div>
      </fieldset>

      <fieldset class="fieldset">
        <legend class="legend">Auszahlung &amp; Handling</legend>
        <div class="cmp-headers">
          <span class="cmp-header-input">Mitbewerber</span>
          <span class="cmp-header-our">easyWerkstatt Payments</span>
        </div>
        <div class="cmp-rows">
          <Row
            label="Fixgebühr je Auszahlung (€)"
            tooltip="Was kostet eine einzelne Auszahlung auf dein Bankkonto? Manche Anbieter berechnen 0,25 € – 1 € pro Payout."
            value={payoutFixed.value}
            onValue={(v) => {
              payoutFixed.value = v;
            }}
            step={0.05}
            ourValue={zeroEur}
          />
          <Row
            label="Prozentgebühr je Auszahlung (%)"
            tooltip="Manche Anbieter berechnen zusätzlich einen Prozentsatz auf das ausgezahlte Volumen."
            value={payoutPercent.value}
            onValue={(v) => {
              payoutPercent.value = v;
            }}
            step={0.05}
            ourValue={zeroPercent}
          />
          <Row
            label="Anzahl Auszahlungen pro Monat"
            tooltip="Wie oft zahlst du dir Geld auf dein Bankkonto aus? Täglich = ca. 22, wöchentlich = 4, monatlich = 1."
            value={payoutsPerMonth.value}
            onValue={(v) => {
              payoutsPerMonth.value = v;
            }}
            step={1}
            ourValue={dash}
          />
          <Row
            label="3DS-Handling-Gebühr je Zahlung (€)"
            tooltip="Manche Acquirer berechnen 0,01–0,05 € pro 3D-Secure-Authentifizierung. Wenn nichts separat ausgewiesen ist: 0 €."
            value={threeDsAvgPerTx.value}
            onValue={(v) => {
              threeDsAvgPerTx.value = v;
            }}
            step={0.01}
            ourValue={zeroEur}
          />
        </div>
      </fieldset>

      <fieldset class="fieldset">
        <legend class="legend">Monatliche Kosten</legend>
        <div class="cmp-headers">
          <span class="cmp-header-input">Mitbewerber</span>
          <span class="cmp-header-our">easyWerkstatt Payments</span>
        </div>
        <div class="cmp-rows">
          <Row
            label="Konto- / Account-Plan (€/Monat)"
            tooltip="Manche Anbieter verlangen eine monatliche Pauschale für ihren Plan oder ihr Geschäftskonto. Übliche Werte: 0–40 €/Monat."
            value={accountMonthly.value}
            onValue={(v) => {
              accountMonthly.value = v;
            }}
            step={1}
            ourValue={zeroEur}
          />
          <Row
            label="Terminal-Hardware (€/Monat)"
            tooltip="Anschaffungs- oder Mietkosten der Kartenterminals auf den Monat umgelegt (z. B. 169 € Anschaffung / 12 Monate ≈ 14 €/Monat). Nur Online-Zahlungen: 0 €."
            value={terminalMonthly.value}
            onValue={(v) => {
              terminalMonthly.value = v;
            }}
            step={1}
            ourValue={zeroEur}
          />
        </div>
      </fieldset>

      <div class="result">
        {result.value === null ? (
          <p class="result-empty">Bitte alle Felder ausfüllen.</p>
        ) : (
          <>
            <div class="compare">
              <article class="compare-card">
                <span class="compare-label">Mitbewerber</span>
                <output class="compare-amount">
                  {formatCurrency(result.value.competitor.total, currency)}
                </output>
                <span class="compare-meta">
                  effektiv {formatPercent(result.value.competitor.effectivePercent)}
                </span>
              </article>
              <article class="compare-card compare-card--accent">
                <span class="compare-label">easyWerkstatt Payments</span>
                <output class="compare-amount">
                  {formatCurrency(result.value.ours.total, currency)}
                </output>
                <span class="compare-meta">
                  effektiv {formatPercent(result.value.ours.effectivePercent)}
                </span>
              </article>
            </div>

            {savingsLine.value !== null && (
              <p class={`savings savings--${savingsLine.value.tone}`}>{savingsLine.value.text}</p>
            )}

            <details class="breakdown">
              <summary>Aufschlüsselung Mitbewerber</summary>
              <ul>
                <li>
                  <span>Privat Debit</span>
                  <span>{formatCurrency(result.value.competitor.privateDebit, currency)}</span>
                </li>
                <li>
                  <span>Privat Kredit</span>
                  <span>{formatCurrency(result.value.competitor.privateCredit, currency)}</span>
                </li>
                <li>
                  <span>Business</span>
                  <span>{formatCurrency(result.value.competitor.business, currency)}</span>
                </li>
                <li>
                  <span>Fixgebühr je Zahlung</span>
                  <span>{formatCurrency(result.value.competitor.fixedFees, currency)}</span>
                </li>
                <li>
                  <span>Auszahlungen</span>
                  <span>{formatCurrency(result.value.competitor.payoutFees, currency)}</span>
                </li>
                <li>
                  <span>3DS-Handling</span>
                  <span>{formatCurrency(result.value.competitor.threeDs, currency)}</span>
                </li>
                <li>
                  <span>Konto-/Account-Plan</span>
                  <span>{formatCurrency(result.value.competitor.accountMonthly, currency)}</span>
                </li>
                <li>
                  <span>Terminal-Hardware</span>
                  <span>{formatCurrency(result.value.competitor.terminalMonthly, currency)}</span>
                </li>
              </ul>
            </details>
          </>
        )}

        <p class="footnote">
          Anteile werden intern auf 100 % normalisiert. Nicht enthalten: Chargeback-, Refund-, FX-
          und Setup-Gebühren — bei easyWerkstatt Payments alle 0&nbsp;€.
        </p>
      </div>
    </div>
  );
};

export default Widget;
